import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { colors, fonts } from '../theme';
import { ellipsisStyle } from '../styles';
import { fitFontSize, roleName } from '../text';
// Row height of the 16 player layout: fewer players keep it instead of stretching rows,
// more players (up to 20, 10 rows) shrink rows and type to fit the stage height.
const ROW_HEIGHT = 121;
const ROSTER_HEIGHT = 1080 - 52 - 56;
const ACTIVE_NOTE = {
    intro: 'говорит',
    shootout: 'называет число',
};
const activePlayerId = (match) => {
    var _a, _b, _c, _d, _e, _f;
    if (match.stage === 'intro')
        return (_b = (_a = match.intro) === null || _a === void 0 ? void 0 : _a.currentPlayerId) !== null && _b !== void 0 ? _b : null;
    if (match.stage === 'vote')
        return (_d = (_c = match.vote) === null || _c === void 0 ? void 0 : _c.currentVoterId) !== null && _d !== void 0 ? _d : null;
    if (match.stage === 'shootout')
        return (_f = (_e = match.shootout) === null || _e === void 0 ? void 0 : _e.currentPlayerId) !== null && _f !== void 0 ? _f : null;
    return null;
};
// The player who opened the current round: the round ends right before them again
const starterId = (match) => {
    var _a, _b, _c, _d;
    if (match.stage === 'intro')
        return (_b = (_a = match.intro) === null || _a === void 0 ? void 0 : _a.starterId) !== null && _b !== void 0 ? _b : null;
    if (match.stage === 'vote')
        return (_d = (_c = match.vote) === null || _c === void 0 ? void 0 : _c.starterId) !== null && _d !== void 0 ? _d : null;
    return null;
};
const teamColor = (player) => {
    if (player.team === 'MAFIA' || player.role === 'MANIAC')
        return colors.danger;
    if (player.team === 'FOOLS')
        return colors.fools;
    return colors.text;
};
const statusMark = (player) => {
    if (player.imprisoned)
        return 'в тюрьме';
    if (player.muted)
        return 'затычка';
    if (player.immunity)
        return 'иммунитет';
    return '';
};
const buildRows = (match) => {
    var _a, _b;
    const final = match.stage === 'final';
    const dark = match.stage === 'night' || match.stage === 'setup';
    const activeId = activePlayerId(match);
    const roundStarterId = starterId(match);
    const votes = new Map(((_b = (_a = match.vote) === null || _a === void 0 ? void 0 : _a.votes) !== null && _b !== void 0 ? _b : []).map((v) => [v.voterId, v.targetId]));
    const received = new Map();
    if (match.stage === 'vote') {
        votes.forEach((targetId) => {
            var _a;
            if (targetId)
                received.set(targetId, ((_a = received.get(targetId)) !== null && _a !== void 0 ? _a : 0) + 1);
        });
    }
    return match.players.map((player) => {
        var _a, _b;
        const active = player.id === activeId;
        const starter = player.id === roundStarterId;
        let note = '';
        let noteColor = colors.muted;
        if (final) {
            note = roleName(player.role);
            noteColor = teamColor(player);
        }
        else if (active) {
            note = (_a = ACTIVE_NOTE[match.stage]) !== null && _a !== void 0 ? _a : '';
            noteColor = colors.ink;
        }
        else if (statusMark(player)) {
            note = statusMark(player);
            noteColor = colors.mark;
        }
        else if (match.stage === 'vote' && votes.has(player.id)) {
            // Whom they voted for is on the left and in the target's badge; a long name would only be cut here
            note = votes.get(player.id) ? 'голос отдан' : 'пас';
        }
        else if (starter) {
            note = 'начал круг';
            noteColor = colors.accent;
        }
        return {
            player,
            note,
            noteColor,
            active,
            starter,
            dimmed: final ? !player.isAlive : dark,
            received: (_b = received.get(player.id)) !== null && _b !== void 0 ? _b : 0,
        };
    });
};
function FoulDots({ fouls, size, onAccent }) {
    const filled = onAccent ? colors.dangerOnAccent : colors.danger;
    const empty = onAccent ? colors.inkFaint : colors.line;
    return (_jsx(_Fragment, { children: [0, 1, 2].map((k) => (_jsx("div", { style: {
                width: size,
                height: size,
                borderRadius: '50%',
                background: k < fouls ? filled : empty,
                flexShrink: 0,
            } }, k))) }));
}
export function Roster({ match }) {
    const rows = buildRows(match);
    const final = match.stage === 'final';
    const rowCount = Math.max(1, Math.ceil(rows.length / 2));
    const rowHeight = Math.min(ROW_HEIGHT, Math.floor(ROSTER_HEIGHT / rowCount));
    const k = rowHeight / ROW_HEIGHT;
    const size = (base, min) => Math.max(min, Math.round(base * k));
    return (_jsx("div", Object.assign({ style: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gridAutoFlow: 'column',
            gridTemplateRows: `repeat(${rowCount}, ${rowHeight}px)`,
            alignContent: 'start',
            columnGap: 32,
            borderLeft: `1px solid ${colors.line}`,
            paddingLeft: 40,
            minHeight: 0,
        } }, { children: rows.map(({ player, note, noteColor, active, starter, dimmed, received }) => (_jsxs("div", Object.assign({ style: {
                display: 'grid',
                // Third column: votes this player has received so far, so the roster reads on its own
                gridTemplateColumns: `${size(64, 52)}px minmax(0, 1fr) auto`,
                alignItems: 'center',
                columnGap: 14,
                // Final: only number and nick fade for the dead, the role keeps full colour so teams
                // stay apart by lightness (civilians L*92, fools L*77, mafia L*67)
                opacity: dimmed && !final ? 0.7 : 1,
                borderBottom: `1px solid ${colors.rowLine}`,
                // Solid fill with dark text: a tint of any colour near the background vanishes at 4-5 m
                background: active ? colors.activeRow : 'transparent',
                color: active ? colors.ink : colors.text,
                // Bar on the round starter stays visible whatever the row note says
                boxShadow: starter ? `inset 6px 0 0 ${active ? colors.ink : colors.accent}` : 'none',
                // Extra left room keeps the starter bar clear of the seat number
                padding: '0 10px 0 22px',
                margin: '0 -10px 0 -22px',
                transition: 'background 300ms, opacity 300ms',
            } }, { children: [_jsx("div", Object.assign({ style: {
                        fontFamily: fonts.serif,
                        fontWeight: 600,
                        fontSize: size(52, 40),
                        lineHeight: 1,
                        opacity: dimmed && final ? 0.6 : 1,
                    } }, { children: player.number })), _jsxs("div", Object.assign({ style: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 } }, { children: [_jsx("div", Object.assign({ style: {
                                // Nick wraps onto a second line instead of being cut; only very long ones shrink to fit two lines
                                fontSize: fitFontSize(player.nick, size(28, 24), 22, 0.6),
                                fontWeight: 500,
                                lineHeight: 1.1,
                                opacity: dimmed && final ? 0.6 : 1,
                                overflowWrap: 'break-word',
                            } }, { children: player.nick })), _jsxs("div", Object.assign({ style: { display: 'flex', alignItems: 'center', gap: 8, minHeight: size(22, 18) } }, { children: [!final && _jsx(FoulDots, { fouls: player.fouls, size: size(12, 10), onAccent: active }), _jsx("div", Object.assign({ style: Object.assign({ fontSize: size(20, 18), fontStyle: 'italic', color: noteColor, marginLeft: final ? 0 : 6, 
                                        // Room for the italic overhang, otherwise overflow: hidden clips the last letter.
                                        paddingRight: 4 }, ellipsisStyle) }, { children: note }))] }))] })), received > 0 && (
                // A filled badge, not a bare digit: next to the other column a bare digit reads as a seat number
                _jsx("div", Object.assign({ style: {
                        width: size(52, 42),
                        height: size(52, 42),
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: fonts.sans,
                        fontWeight: 800,
                        fontSize: size(30, 24),
                        background: active ? colors.ink : colors.accent,
                        color: active ? colors.accent : colors.ink,
                    } }, { children: received })))] }), player.id))) })));
}
